import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TABLE_ACTION_MIN_WIDTH,
  TableActionBar,
  TruncatedCell,
  tableActionsColumnClass,
  type DataTableColumn,
} from './table';
import StatusBadge from './ui/StatusBadge';
import {
  filterKnownChats,
  knownChatEntityLabel,
  knownChatLabel,
  knownChatSourceLabel,
} from '@/utils/knownChatsDisplay';

export type KnownChat = {
  chat_id: number;
  username?: string | null;
  first_name?: string | null;
  source?: string | null;
  access_hash?: number | null;
  entity_type?: string | null;
};

type Props = {
  chats: KnownChat[];
  onClose: () => void;
  onUse: (chat: KnownChat) => void | Promise<void>;
  onRefresh?: () => void | Promise<void>;
  creatingChatId?: number | null;
  loading?: boolean;
  refreshing?: boolean;
  notice?: string | null;
  refreshError?: string | null;
  lastRefreshedAt?: string | null;
};

function formatRefreshedAt(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
}

function sourceVariant(source: string | null | undefined): 'info' | 'neutral' {
  if (source?.toLowerCase().includes('telethon')) return 'info';
  return 'neutral';
}

export default function KnownChatsModal({
  chats,
  onClose,
  onUse,
  onRefresh,
  creatingChatId = null,
  loading = false,
  refreshing = false,
  notice = null,
  refreshError = null,
  lastRefreshedAt = null,
}: Props) {
  const [query, setQuery] = useState('');

  useEffect(() => {
    setQuery('');
  }, []);

  const filteredCount = useMemo(() => filterKnownChats(chats, query).length, [chats, query]);
  const refreshedLabel = formatRefreshedAt(lastRefreshedAt);
  const showEmpty = chats.length === 0;
  const busyNotice =
    notice?.includes('busy') || refreshError?.toLowerCase().includes('busy') ? notice || refreshError : null;

  const copyId = useCallback(async (id: number) => {
    try {
      await navigator.clipboard.writeText(String(id));
    } catch {
      /* clipboard unavailable */
    }
  }, []);

  const renderActions = useCallback(
    (chat: KnownChat) => (
      <TableActionBar>
        <button type="button" className="btn btn-sm" onClick={() => void copyId(chat.chat_id)}>
          Copy ID
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={loading || creatingChatId === chat.chat_id}
          onClick={() => void onUse(chat)}
        >
          {creatingChatId === chat.chat_id ? 'Adding…' : 'Use'}
        </button>
      </TableActionBar>
    ),
    [copyId, creatingChatId, loading, onUse],
  );

  const columns = useMemo<DataTableColumn<KnownChat>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        width: 220,
        minWidth: 160,
        maxWidth: 320,
        sortable: true,
        sortValue: (chat) => knownChatLabel(chat),
        csvValue: (chat) => knownChatLabel(chat),
        render: (chat) => <TruncatedCell value={knownChatLabel(chat)} />,
      },
      {
        id: 'chat_id',
        header: 'Chat ID',
        width: 150,
        minWidth: 120,
        maxWidth: 200,
        sortable: true,
        sortValue: (chat) => chat.chat_id,
        csvValue: (chat) => chat.chat_id,
        render: (chat) => <TruncatedCell value={String(chat.chat_id)} copyable />,
      },
      {
        id: 'source',
        header: 'Source',
        width: 120,
        minWidth: 100,
        maxWidth: 140,
        resizable: false,
        sortable: true,
        sortValue: (chat) => knownChatSourceLabel(chat.source),
        render: (chat) => (
          <StatusBadge variant={sourceVariant(chat.source)}>{knownChatSourceLabel(chat.source)}</StatusBadge>
        ),
      },
      {
        id: 'entity',
        header: 'Entity',
        width: 110,
        minWidth: 90,
        maxWidth: 130,
        resizable: false,
        sortable: true,
        sortValue: (chat) => knownChatEntityLabel(chat.entity_type),
        render: (chat) => (
          <StatusBadge variant="neutral">{knownChatEntityLabel(chat.entity_type)}</StatusBadge>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        width: TABLE_ACTION_MIN_WIDTH.knownChats,
        minWidth: TABLE_ACTION_MIN_WIDTH.knownChats,
        resizable: false,
        actions: true,
        className: tableActionsColumnClass('knownChats'),
        render: (chat) => renderActions(chat),
      },
    ],
    [renderActions],
  );

  const renderMobileCard = useCallback(
    (chat: KnownChat, actions: React.ReactNode) => (
      <article className="known-chats-mobile-card">
        <h3 className="known-chats-mobile-card__title">{knownChatLabel(chat)}</h3>
        <p className="known-chats-mobile-card__meta">
          <StatusBadge variant={sourceVariant(chat.source)}>{knownChatSourceLabel(chat.source)}</StatusBadge>
          <span aria-hidden="true"> · </span>
          <StatusBadge variant="neutral">{knownChatEntityLabel(chat.entity_type)}</StatusBadge>
        </p>
        <p className="known-chats-mobile-card__id">
          Chat ID: <span className="known-chats-mobile-card__id-value">{chat.chat_id}</span>
        </p>
        <div className="known-chats-mobile-card__actions">{actions}</div>
      </article>
    ),
    [],
  );

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal card known-chats-modal"
        role="dialog"
        aria-labelledby="known-chats-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="known-chats-modal__header">
          <h2 id="known-chats-title">Known chats</h2>
          <div className="known-chats-modal__header-actions">
            {onRefresh ? (
              <button
                type="button"
                className="btn btn-sm"
                disabled={loading || refreshing}
                onClick={() => void onRefresh()}
              >
                {refreshing ? 'Refreshing…' : 'Refresh chats'}
              </button>
            ) : null}
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
              Close
            </button>
          </div>
        </header>

        {notice && !showEmpty ? (
          <div className="alert alert-info known-chats-modal__notice" role="status">
            {notice}
          </div>
        ) : null}

        {refreshError && !busyNotice ? (
          <div className="alert alert-error known-chats-modal__notice" role="alert">
            {refreshError}
          </div>
        ) : null}

        {refreshedLabel ? (
          <p className="muted known-chats-modal__meta">Last refreshed: {refreshedLabel}</p>
        ) : null}

        {showEmpty ? (
          <div className="known-chats-modal__empty" role="status">
            {busyNotice ? (
              <p>{busyNotice}</p>
            ) : (
              <>
                <p className="known-chats-modal__empty-title">No Telegram chats cached yet.</p>
                <p className="muted">Refresh chats from the authorized Telethon session.</p>
              </>
            )}
            <div className="known-chats-modal__empty-actions">
              {onRefresh ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={loading || refreshing}
                  onClick={() => void onRefresh()}
                >
                  {refreshing ? 'Refreshing…' : 'Refresh chats'}
                </button>
              ) : null}
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="known-chats-modal__body">
              <DataTable
                columns={columns}
                rows={chats}
                getRowKey={(chat) => String(chat.chat_id)}
                stickyHeader
                responsiveCards
                renderMobileCard={renderMobileCard}
                className="known-chats-modal__data-table"
                toolbar={{
                  searchPlaceholder: 'Filter by name, @username, or chat ID',
                  searchValue: query,
                  onSearchChange: setQuery,
                }}
                emptyState={<span className="muted">No matches for your search.</span>}
              />
            </div>

            <p className="muted known-chats-modal__hint" data-testid="known-chats-count">
              {filteredCount} of {chats.length} chat{chats.length === 1 ? '' : 's'} shown
            </p>
          </>
        )}
      </div>
    </div>
  );
}
